import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Wallet, Info, CheckCircle } from "lucide-react";

export default function EmergencyFund() {
  const [monthlyExpenses, setMonthlyExpenses] = useState(2500);
  const [months, setMonths] = useState(6);
  const [currentSavings, setCurrentSavings] = useState(5000);
  const [monthlySaving, setMonthlySaving] = useState(300);

  const target = monthlyExpenses * months;
  const gap = Math.max(0, target - currentSavings);
  const monthsToGoal = gap > 0 ? Math.ceil(gap / monthlySaving) : 0;
  const progress = Math.min((currentSavings / target) * 100, 100);

  const fmt = (n) =>
    new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP", maximumFractionDigits: 0 }).format(n);

  const EXPENSE_ITEMS = [
    { label: "Rent / Mortgage", suggested: 1200 },
    { label: "Utilities & Bills", suggested: 200 },
    { label: "Groceries", suggested: 400 },
    { label: "Transport", suggested: 250 },
    { label: "Insurance", suggested: 150 },
    { label: "Other Essentials", suggested: 300 },
  ];

  return (
    <div className="bg-slate-950 min-h-screen pt-16">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <Link
          to="/tools"
          data-testid="back-to-tools"
          className="inline-flex items-center gap-2 text-slate-400 hover:text-white text-sm mb-8 transition-colors group"
        >
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
          Back to Tools
        </Link>

        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center">
            <Wallet className="w-5 h-5 text-amber-400" />
          </div>
          <span className="text-amber-400 text-sm font-medium">Planning</span>
        </div>
        <h1
          data-testid="emergency-fund-title"
          className="font-outfit text-3xl sm:text-4xl font-bold text-white mb-3 tracking-tight"
        >
          Emergency Fund Calculator
        </h1>
        <p className="text-slate-400 mb-10">
          Calculate how much you need in your financial safety net and how long it'll take to get there.
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* Inputs */}
          <div data-testid="emergency-inputs" className="lg:col-span-2 space-y-4">
            <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-6 space-y-5">
              <h2 className="font-outfit font-semibold text-white text-lg">Your Details</h2>

              <div>
                <label className="block text-slate-400 text-sm mb-1.5">Monthly Essential Expenses</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">£</span>
                  <input
                    type="number"
                    data-testid="input-expenses"
                    value={monthlyExpenses}
                    onChange={(e) => setMonthlyExpenses(Number(e.target.value))}
                    className="w-full bg-slate-950 border border-slate-700 focus:border-amber-500 focus:ring-1 focus:ring-amber-500/30 rounded-lg pl-7 pr-4 py-2.5 text-white text-sm outline-none transition-colors"
                  />
                </div>
              </div>

              <div>
                <label className="block text-slate-400 text-sm mb-1.5">
                  Months of Cover: <span className="text-amber-400 font-medium">{months} months</span>
                </label>
                <input
                  type="range"
                  data-testid="input-months"
                  min="1" max="12" step="1"
                  value={months}
                  onChange={(e) => setMonths(Number(e.target.value))}
                  className="w-full accent-amber-500 cursor-pointer"
                />
                <div className="flex justify-between text-slate-600 text-xs mt-1">
                  <span>1 mo</span><span>12 mo</span>
                </div>
              </div>

              <div>
                <label className="block text-slate-400 text-sm mb-1.5">Current Savings Towards Goal</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">£</span>
                  <input
                    type="number"
                    data-testid="input-savings"
                    value={currentSavings}
                    onChange={(e) => setCurrentSavings(Number(e.target.value))}
                    className="w-full bg-slate-950 border border-slate-700 focus:border-amber-500 focus:ring-1 focus:ring-amber-500/30 rounded-lg pl-7 pr-4 py-2.5 text-white text-sm outline-none transition-colors"
                  />
                </div>
              </div>

              <div>
                <label className="block text-slate-400 text-sm mb-1.5">Monthly Saving Towards Fund</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">£</span>
                  <input
                    type="number"
                    data-testid="input-monthly-saving"
                    value={monthlySaving}
                    onChange={(e) => setMonthlySaving(Number(e.target.value))}
                    className="w-full bg-slate-950 border border-slate-700 focus:border-amber-500 focus:ring-1 focus:ring-amber-500/30 rounded-lg pl-7 pr-4 py-2.5 text-white text-sm outline-none transition-colors"
                  />
                </div>
              </div>
            </div>

            {/* Expense guide */}
            <div className="bg-slate-900/30 border border-slate-800/50 rounded-xl p-5">
              <p className="text-slate-400 text-sm font-medium mb-3">UK Average Essentials</p>
              <div className="space-y-2">
                {EXPENSE_ITEMS.map((item) => (
                  <div key={item.label} className="flex justify-between text-xs">
                    <span className="text-slate-500">{item.label}</span>
                    <span className="text-slate-400">~{fmt(item.suggested)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Results */}
          <div data-testid="emergency-results" className="lg:col-span-3 space-y-4">
            {/* Target */}
            <div className="bg-amber-500/5 border border-amber-500/20 rounded-2xl p-6">
              <p className="text-slate-400 text-sm mb-1">Emergency Fund Target</p>
              <p data-testid="result-target" className="font-outfit text-4xl font-bold text-amber-400">
                {fmt(target)}
              </p>
              <p className="text-slate-500 text-sm mt-1">{months} months × {fmt(monthlyExpenses)}</p>
            </div>

            {/* Progress */}
            <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-6">
              <div className="flex justify-between items-center mb-3">
                <p className="text-slate-400 text-sm">Progress</p>
                <p className="text-amber-400 text-sm font-medium">{Math.round(progress)}%</p>
              </div>
              <div className="w-full bg-slate-800 rounded-full h-3 overflow-hidden mb-2">
                <div
                  data-testid="result-progress-bar"
                  className="bg-amber-500 h-3 rounded-full transition-all duration-500"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-slate-500">{fmt(currentSavings)} saved</span>
                <span className="text-slate-500">{fmt(target)} target</span>
              </div>
            </div>

            {/* Gap & Timeline */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-5">
                <p className="text-slate-500 text-xs mb-1.5">Amount Remaining</p>
                <p data-testid="result-gap" className="font-outfit text-2xl font-bold text-white">
                  {gap === 0 ? (
                    <span className="text-emerald-400">Goal Met!</span>
                  ) : fmt(gap)}
                </p>
              </div>
              <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-5">
                <p className="text-slate-500 text-xs mb-1.5">Months to Goal</p>
                <p data-testid="result-months" className="font-outfit text-2xl font-bold text-white">
                  {monthsToGoal === 0 ? (
                    <span className="text-emerald-400 flex items-center gap-1">
                      <CheckCircle className="w-5 h-5" /> Done
                    </span>
                  ) : `${monthsToGoal} months`}
                </p>
              </div>
            </div>

            {/* Status */}
            {gap === 0 ? (
              <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-4 flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                <p className="text-emerald-400 text-sm font-medium">
                  You've reached your emergency fund goal!
                </p>
              </div>
            ) : (
              <div className="flex items-start gap-2 bg-slate-900/30 border border-slate-800/50 rounded-xl p-4">
                <Info className="w-4 h-4 text-slate-500 flex-shrink-0 mt-0.5" />
                <p className="text-slate-500 text-xs leading-relaxed">
                  Financial experts recommend 3–6 months of essential expenses. Keep your emergency fund in an easy-access savings account (not invested).
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
